import { type Phase } from '@chess/api-model';
import { isExploring, type ReviewState } from '@chess/api-review';
import type { ConstraintRegistry } from '@chess/api-constraints';
import { phaseOfPly, type Followed } from './follow-stage.ts';

// The stage tabs and what a click on one does. A click on a stage tab, not a
// tab opened by following the game, moves the board to the stage's first
// position (`Stages.entryPly`: the game's final position when it never
// reached the stage); the board stays put while the reader explores a line
// or the board's Controls constraint holds it, as a puzzle does.

export const SLOT = 'review.sidePanel';
export const TABS: ReadonlyArray<{ phase: Phase; title: string; order: number }> = [
  { phase: 'opening', title: 'Opening', order: 10 },
  { phase: 'middlegame', title: 'Middlegame', order: 13 },
  { phase: 'ending', title: 'Ending', order: 16 },
];

export const isPhase = (id: string): id is Phase => TABS.some((t) => t.phase === id);

/** Sections render by their registration `order`, lowest first: the stage's own content, then puzzles, then history. */
export const byOrder = <T extends { defaults?: { order?: number } }>(items: T[]): T[] =>
  [...items].sort((a, b) => (a.defaults?.order ?? 0) - (b.defaults?.order ?? 0));

/** The board's Controls constraint holds it (a range, or back or forward off): neither following nor a tab click moves it. */
export function controlsHeld(registry: Pick<ConstraintRegistry, 'current'> | undefined, state: ReviewState): boolean {
  if (!registry || !state.scope) return false;
  const controls = registry.current(state.scope, 'controls');
  return controls.range !== undefined || !controls.back || !controls.forward;
}

/** Where a click on a stage tab takes the board; undefined without a game, while exploring a line, or while the controls are held. */
export function entryPly(state: ReviewState, phase: Phase, held: boolean): number | undefined {
  if (!state.game || !state.stages || isExploring(state) || held) return undefined;
  return state.stages.entryPly(phase);
}

/**
 * A click on a stage tab: the ply to go to, or undefined to leave the board.
 * The jump lands where the board would follow to some stage, possibly not
 * the clicked one (a game that never reached the ending lands on its final
 * position, in the middlegame), so that stage is recorded in `followed` as
 * already followed, and the jump itself never switches the tab the reader
 * just clicked.
 */
export function tabClick(state: ReviewState, phase: Phase, held: boolean, followed: Followed): number | undefined {
  const ply = entryPly(state, phase, held);
  if (ply === undefined || !state.game) return undefined;
  followed.gameId = state.game.id;
  followed.phase = phaseOfPly({ ...state, ply }) ?? followed.phase;
  return ply;
}
